import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import TextTypingAnimation from '../components/Subtitle';
import ResumeButton from './ResumeButton';
import { motion } from "framer-motion";
import Spinner from "./Spinner";
import SocialIcons from './SocialIcons';

// Container for the section
const CoverSection = styled.div`
  position: relative; 
  background-color: #020202;
  padding: 100px 0;
  height: 100vh;
  display: flex;
  flex-direction: row; /* Default to row layout */
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: 980px) and (orientation: landscape) {
    flex-direction: column; /* Column layout in landscape mode */
    padding: 50px 0; 
  }

  @media only screen and (max-width: 1024px) and (orientation: portrait) {
    flex-direction: row-reverse; /* Column layout in portrait mode */
    padding: 30px 0;
  }
`;

// Heading for the section
const Heading = styled.h2`
  font-family: 'Dancing Script', cursive;
  color: silver;
  font-size: 2.4rem;
  font-weight: normal;
  /* margin-top: 1rem; */

  @media only screen and (max-width: 980px) and (orientation: landscape) {
    font-size: 1.6rem;
    margin-top: 0;
  }

  @media only screen and (max-width: 768px) and (orientation: portrait) {
    font-size: 1.8rem; /* Adjust font size for portrait mode */
    margin-top: 0;
  }
`;

// Container for the image and text
const Contents = styled.div`
  display: flex;
  flex-direction: row; /* Default to row layout */
  align-items: center;
  justify-content: center;
  gap: 40px;  /* Adjust spacing between image and text */
  width: 74%; /* Adjust width for better spacing */
  /* z-index: 999; */
  @media only screen and (max-width: 1024px) and (orientation: landscape) {
    /* flex-direction: column; Column layout in landscape mode */
    width: 72%;
  }

  @media only screen and (max-width: 1024px) and (orientation: portrait) {
    flex-direction: column; /* Column layout in portrait mode */
    width: 80%;
  }
`;

// Glow animation for the image
const glowAnimation = keyframes`
  0% {
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.01);
  }
  50% {
    box-shadow: 0 0 70px rgba(190, 156, 255, 0.1);
  }
  100% {
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.01);
  }
`;

const CoverImage = styled.img`
  width: 347px;
  height: auto;
  min-width: 341px;
  border: 1.4rem solid rgba(255, 255, 255, 0);
  border-radius: 50%;
  animation: ${glowAnimation} 3s infinite;
  opacity: 97%;

  @media screen and (max-width: 1366px) {
    width: 500px; /* Ensure the image scales well on tablets and smaller screens */
    /* min-width: 300px; Avoid too small images */
  }

  @media screen and (max-width: 768px) {
    width: 60vw; /* Adjust to be responsive on smaller tablets and landscape phones */
    min-width: 170px; 
  }

  @media screen and (min-width: 768px) and (orientation: portrait) {
    width: 500px; /* Large size for larger screens in portrait mode */
    /* min-width: 200px;  */
  }

  @media screen and (max-width: 500px) and (orientation: portrait) {
    width: 70vw; /* Larger width in portrait mode on smaller screens */
    /* min-width: 150px; */
  }

  @media screen and (max-width: 480px) {
    width: 50vw; /* Responsive width on mobile devices */
    min-width: 270px;
  }
`;

const GradientText = styled.p`
  font-family: 'Runtoe';
  font-size: 5.6rem; /* Default font size */
  font-weight: bold;
  background: linear-gradient(45deg, #FF5a6A, #FFD93D, #6BCB77, #4D96FF);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
  /* margin-bottom: 5px; */
  text-align: left;
  
  @media only screen and (max-width: 1024px) {
    font-size: 3.7rem; /* Adjust size for tablets and smaller screens */
    /* margin-bottom: 0.5rem; */
  }
  
    @media only screen and (max-width: 1024px) and (orientation: portrait) {
      font-size: 9.2vw; /* Responsive size for portrait mode on medium screens */
      margin-bottom: 0;
    }
  
  @media only screen and (max-width: 700px) and (orientation: landscape) {
    font-size: 30px; /* Size adjustment for landscape mode on smaller devices */
    margin-bottom: 0;
  }

  @media only screen and (max-width: 480px) {
    font-size: 24px; /* Ensure readability on very small screens */
    /* margin-bottom: 0.5rem; */
  }
`;


// Main text
const CoverText = styled.p`
  color: #ccc;
  font-size: 1.7rem;
  text-align: justify;
  line-height: 1.6;
  margin-bottom: 0.47rem;
  @media screen and (max-width: 768px) {
    font-size: 1.2rem;
  }
  @media screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait)  {
    font-size: 2.1rem;
  }
`;

// Typing text style
const TypingText = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 2.1rem;
  color: #eee;
  margin-bottom: 1rem;
  text-align: left;

  @media screen and (max-width: 768px) {
    font-size: 2.4rem;
    margin-bottom: 0.5rem;
  }

  @media only screen and (max-width: 768px) and (orientation: landscape) {
    font-size: 1.6rem;
  }
`;

const Cover = () => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = "/profile-pic-10.png";
    img.onload = () => setIsImageLoaded(true);
  }, []);

  const handleResumeButtonClick = () => {
    trackButtonClick('Resume button Clicked');
  };

  return (
    <CoverSection>
      <Contents>
        <motion.div
          initial={{ x: "-100vw" }}
          animate={{ x: 0 }}
          transition={{ duration: 1, type: "spring", stiffness: 50 }}
        >
          {isImageLoaded ? (
            <CoverImage src="/profile-pic-10.png" alt="Cover Image" />) : (
            <Spinner size={25} type='thin' />
          )}
        </motion.div>
        <div style={{ zIndex: 999 }}>
          <GradientText>I'm Sachin Kumar</GradientText>
          <Heading>Welcome to my Portfolio!</Heading>
          <CoverText>I’m a 2021 CSE Graduate, an aspiring Full-Stack Developer
            with a passion for Software development and a vision to lead and innovate in the ever-evolving tech landscape, specialize in react and springboot development.
          </CoverText>
          {/* <TypingText><span>I'm </span><TextTypingAnimation /></TypingText> */}
          <span>
            <SocialIcons />
            <ResumeButton onClick={handleResumeButtonClick} resumeUrl="/Resume-Sachin.pdf">View Resume</ResumeButton>
          </span>
        </div>
      </Contents>
    </CoverSection>
  );
};

export default Cover;
